import React, { useCallback, useState } from 'react'
import { useInSession } from '../../../hooks/useInSession.js'
import { usePrep } from '../../../hooks/usePrep.js'
import { ConfirmDialog } from '../../../ui'

import { TalkingPointsCard } from '../../../cards/inSession/kinds/TalkingPointsCard.jsx'
import { DecisionsCard } from '../../../cards/inSession/kinds/DecisionsCard.jsx'
import { ParkingLotCard } from '../../../cards/inSession/kinds/ParkingLotCard.jsx'
import { KeyTakeawayCard } from '../../../cards/inSession/kinds/KeyTakeawayCard.jsx'
import { NextPrioritiesCard } from '../../../cards/inSession/kinds/NextPrioritiesCard.jsx'
import { SharedNotesCard } from '../../../cards/inSession/kinds/SharedNotesCard.jsx'
import { CommitmentsCard } from '../../../cards/inSession/kinds/CommitmentsCard.jsx'
import { FeedbackGivenCard } from '../../../cards/inSession/kinds/FeedbackGivenCard.jsx'
import { AgreementsCard } from '../../../cards/inSession/kinds/AgreementsCard.jsx'
import { QuestionsRaisedCard } from '../../../cards/inSession/kinds/QuestionsRaisedCard.jsx'
import { OneChangeCard } from '../../../cards/inSession/kinds/OneChangeCard.jsx'
import { SessionRatingCard } from '../../../cards/inSession/kinds/SessionRatingCard.jsx'

/**
 * InSessionSection — renders the in-session cards grid.
 *
 * In draft/ready: shows an empty placeholder ("unlocks when ready").
 * In in-session: full editable 12-card grid.
 * In completed/cancelled: all cards locked (disabled=true).
 *
 * Props:
 *   session         object — session object
 *   me              object — current user
 *   disabled        boolean — true when locked (completed/cancelled)
 *   inSessionVisible boolean — true when in-session section should show cards
 *   isManagerView   boolean
 *   onComplete      function(sessionId, userId)
 *   onCancel        function(sessionId, userId)
 */
export function InSessionSection({
  session,
  me,
  disabled = false,
  inSessionVisible = false,
  isManagerView = false,
  onComplete,
  onCancel,
}) {
  const sessionId = session?.sessionId
  const prepOwnerUserId = session?.userId
  const prepId = session?.prepId

  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [completeLoading, setCompleteLoading] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)

  const {
    rows,
    loading,
    error,
    getNoteText,
    addTalkingPoint,
    updateTalkingPoint,
    removeTalkingPoint,
    addDecision,
    updateDecision,
    removeDecision,
    updateNote,
    pullFromPrep,
  } = useInSession(sessionId)

  const { prep: prepTree } = usePrep(prepId ?? null, prepOwnerUserId ?? null, {
    enabled: !!(prepId && prepOwnerUserId),
  })

  const prepBullets = React.useMemo(() => {
    if (!prepTree?.cards) return []
    return prepTree.cards.flatMap((card) =>
      (card.bullets ?? []).map((b) => ({ ...b, cardKind: card.kind })),
    )
  }, [prepTree])

  const talkingPoints = rows?.talkingPoints ?? []
  const decisions = rows?.decisions ?? []
  const pullEdges = rows?.pullEdges ?? []

  const handleTPStatusUpdate = useCallback(
    (talkingPointId, status) => updateTalkingPoint(talkingPointId, { status }),
    [updateTalkingPoint],
  )
  const handleTPAdd = useCallback((text = '') => addTalkingPoint({ text }), [addTalkingPoint])
  const handleTPRemove = useCallback((id) => removeTalkingPoint(id), [removeTalkingPoint])
  const handlePullFromPrep = useCallback((bulletId, text) => pullFromPrep(bulletId, text), [pullFromPrep])
  const handleDecisionAdd = useCallback((input) => addDecision(input), [addDecision])
  const handleDecisionUpdate = useCallback((id, input) => updateDecision(id, input), [updateDecision])
  const handleDecisionRemove = useCallback((id) => removeDecision(id), [removeDecision])

  async function handleComplete() {
    setCompleteLoading(true)
    try {
      await onComplete?.(sessionId, session?.userId)
      setShowCompleteConfirm(false)
    } catch {
      // Leave dialog open
    } finally {
      setCompleteLoading(false)
    }
  }

  async function handleCancel() {
    setCancelLoading(true)
    try {
      await onCancel?.(sessionId, session?.userId)
      setShowCancelConfirm(false)
    } catch {
      // Leave dialog open
    } finally {
      setCancelLoading(false)
    }
  }

  // Pre-start: show placeholder
  if (!inSessionVisible) {
    return (
      <div className="empty-big" data-testid="in-session-placeholder">
        <p className="empty-big__text viewing-notice">
          Unlocks when the session starts.
        </p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="in-session-section__loading" aria-live="polite" data-testid="in-session-loading">
        Loading session…
      </div>
    )
  }

  if (error) {
    return (
      <div className="in-session-section__error" role="alert" data-testid="in-session-error">
        Failed to load session. Please refresh.
      </div>
    )
  }

  return (
    <div className="in-session-section" data-testid="in-session-section">
      {/* ── Lifecycle controls (active session, non-manager) ── */}
      {!disabled && !isManagerView && (
        <div className="in-session-section__actions" data-testid="in-session-actions">
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => setShowCancelConfirm(true)}
          >
            Cancel 1:1
          </button>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => setShowCompleteConfirm(true)}
          >
            Complete session
          </button>
        </div>
      )}

      {/* ── Confirm dialogs ── */}
      <ConfirmDialog
        open={showCompleteConfirm}
        title="Complete this session?"
        message="The session will be locked for editing. Your notes and commitments are saved."
        confirmLabel={completeLoading ? 'Completing…' : 'Complete session'}
        cancelLabel="Cancel"
        onConfirm={handleComplete}
        onCancel={() => setShowCompleteConfirm(false)}
      />
      <ConfirmDialog
        open={showCancelConfirm}
        title="Cancel this session?"
        message="This marks the session as cancelled and locks it. It cannot be reopened. Your notes are preserved but the session cannot be edited."
        confirmLabel={cancelLoading ? 'Cancelling…' : 'Cancel session'}
        cancelLabel="Keep session"
        onConfirm={handleCancel}
        onCancel={() => setShowCancelConfirm(false)}
        tone="danger"
      />

      {/* ── 12-card grid ── */}
      <div className="cards-grid" data-testid="in-session-grid">
        <TalkingPointsCard
          talkingPoints={talkingPoints}
          pullEdges={pullEdges}
          prepBullets={prepBullets}
          onUpdateStatus={handleTPStatusUpdate}
          onAdd={handleTPAdd}
          onRemove={handleTPRemove}
          onPullFromPrep={handlePullFromPrep}
          disabled={disabled}
        />
        <DecisionsCard
          decisions={decisions}
          onAdd={handleDecisionAdd}
          onUpdate={handleDecisionUpdate}
          onRemove={handleDecisionRemove}
          disabled={disabled}
        />
        <ParkingLotCard
          text={getNoteText('parking-lot')}
          onChange={(text) => updateNote('parking-lot', text)}
          disabled={disabled}
        />
        <KeyTakeawayCard
          text={getNoteText('key-takeaway')}
          onChange={(text) => updateNote('key-takeaway', text)}
          disabled={disabled}
        />
        <NextPrioritiesCard sessionId={sessionId} disabled={disabled} />
        <SharedNotesCard
          text={getNoteText('shared-notes')}
          onChange={(text) => updateNote('shared-notes', text)}
          disabled={disabled}
        />
        <CommitmentsCard
          text={getNoteText('commitments')}
          onChange={(text) => updateNote('commitments', text)}
          disabled={disabled}
        />
        <FeedbackGivenCard
          text={getNoteText('feedback-given')}
          onChange={(text) => updateNote('feedback-given', text)}
          disabled={disabled}
          isManagerView={isManagerView}
        />
        <AgreementsCard
          text={getNoteText('agreements')}
          onChange={(text) => updateNote('agreements', text)}
          disabled={disabled}
        />
        <QuestionsRaisedCard
          text={getNoteText('questions')}
          onChange={(text) => updateNote('questions', text)}
          disabled={disabled}
        />
        <OneChangeCard
          text={getNoteText('one-change')}
          onChange={(text) => updateNote('one-change', text)}
          disabled={disabled}
        />
        <SessionRatingCard disabled={disabled} />
      </div>
    </div>
  )
}
